import React from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { computeInboxModel, channelPerspective } from "../../utils/inboxRoles";
import describeThreadContext from "./threadContext";

// ThreadContextCard (D8/D9) — the origin/context card pinned at the top of every
// transaction-born thread (Bible D8/D9, frame 5e). Renders the lane pill, the
// VIEWER-RELATIVE context line, and the owner-only "View request/listing" link.
// Returns null for a plain DM / identity-only thread (never a faked card).
//
// Self-contained: it reads the channel + the viewer's identity and resolves
// perspective + the other party itself, so CustomWindow mounts <ThreadContextCard/>
// with no props. All wording/owner logic lives in the pure ./threadContext helper.

// Intent pill palette (frame 1f / 5e — the second, intent pill next to the lane
// pill). Unknown labels fall back to the neutral swatch.
const INTENT_PILL = {
  "Player Opportunity": { bg: "#EEF0FF", fg: "#444CE7" },
  "Discuss about Player": { bg: "#F4F0FF", fg: "#6941C6" },
  Networking: { bg: "#E5F5F6", fg: "#0E7C86" },
  "Partnership Request": { bg: "#EEF0FF", fg: "#444CE7" },
  Partnership: { bg: "#EEF0FF", fg: "#444CE7" },
  Manual: { bg: "#F2F4F7", fg: "#475467" },
};

const ArrowIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2 6h8M7 3l3 3-3 3"
      stroke="#1466D8"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ThreadContextCard = () => {
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const navigate = useNavigate();
  const { user, roles } = useSelector((state) => state.auth);

  const data = channel?.data || null;
  const viewerAccountId = user?.account_id ?? user?.id ?? null;

  const caps = React.useMemo(
    () => computeInboxModel({ roles, user }),
    [roles, user]
  );

  // Other party identity (person + club) for the counterpart wording slots.
  const otherUser = React.useMemo(() => {
    const users = Array.isArray(data?.users) ? data.users : [];
    return users.find((u) => String(u?.id) !== String(client?.userID)) || null;
  }, [data, client?.userID]);

  if (!data) return null;

  const perspective = channelPerspective(
    data,
    { agentCapable: caps.agentCapable, clubCapable: caps.clubCapable },
    client?.userID
  );

  const descriptor = describeThreadContext({
    channelData: data,
    viewerAccountId,
    perspective,
    otherName: otherUser?.name || otherUser?.full_name || otherUser?.email || null,
    otherClubName: otherUser?.club_name || null,
  });

  // No transactional origin → identity-only header (Bible V5 fallback).
  if (!descriptor || !descriptor.line) return null;

  const { pill, line, view } = descriptor;
  const intentLabel =
    data.tp_intent_label || data.request_intent_label || null;
  const intentColors = intentLabel
    ? INTENT_PILL[intentLabel] || INTENT_PILL.Manual
    : null;

  return (
    <div className="flex-none px-3 pt-2 pb-1 bg-[#F8F9FB]" data-testid="thread-context-card">
      <div
        className="flex items-start gap-3 rounded-[12px] border bg-white"
        style={{ borderColor: "#D6E6FF", padding: "10px 12px" }}
      >
        {/* Star icon tile (5e) */}
        <span
          aria-hidden="true"
          className="flex flex-shrink-0 items-center justify-center rounded-[9px]"
          style={{ width: 34, height: 34, background: "#EEF0FF", color: "#444CE7" }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.7}
              d="M12 3l2.09 4.26L18.9 8l-3.45 3.36.82 4.78L12 13.9l-4.27 2.24.82-4.78L5.1 8l4.81-.74L12 3z"
            />
          </svg>
        </span>

        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
          {/* Pills: intent (when present) + lane pill */}
          <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
            {intentLabel && intentColors && (
              <span
                style={{
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: intentColors.bg,
                  color: intentColors.fg,
                  fontSize: 10.5,
                  fontWeight: 600,
                }}
              >
                {intentLabel}
              </span>
            )}
            {pill && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: pill.bg,
                  color: pill.fg,
                  fontSize: 10.5,
                  fontWeight: 600,
                }}
              >
                {pill.label}
              </span>
            )}
          </div>

          {/* Viewer-relative D9 context line */}
          <span className="text-[13px] font-semibold text-[#101828]" title={line}>
            {line}
          </span>

          {/* Owner-only view link (lane-specific label) */}
          {view && (
            <button
              type="button"
              onClick={() => navigate(view.path)}
              className="mt-0.5 inline-flex w-fit items-center gap-1 text-[11.5px] font-semibold text-[#1466D8]"
              data-testid="thread-context-view-link"
            >
              {view.label}
              <ArrowIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreadContextCard;
